/**
 * OAuth Helper Utilities
 *
 * Shared utilities for OAuth flow handling across web applications.
 * These helpers ensure consistent, secure OAuth implementations.
 */

/**
 * OAuth callback parameters parsed from URL
 */
export interface OAuthCallbackParams {
	code: string | null;
	state: string | null;
	error: string | null;
	errorDescription: string | null;
}

/**
 * OAuth error classification result
 */
export interface OAuthErrorClassification {
	/** Whether this is a user-initiated cancellation (not a real error) */
	isUserCancellation: boolean;
	/** Whether this is a redirect/configuration error */
	isConfigurationError: boolean;
	/** Whether this is a PKCE-related error */
	isPKCEError: boolean;
	/** Whether this is an authorization code error */
	isCodeError: boolean;
	/** Whether this error can be recovered by restarting the OAuth flow */
	isRecoverable: boolean;
	/** Human-readable error message */
	message: string;
}

/**
 * Options for checking whether to initiate OAuth redirect
 */
export interface OAuthRedirectCheckOptions {
	isInitializing: boolean;
	isProcessingCallback: boolean;
	isAuthenticated: boolean;
	isLoading: boolean;
	hasOAuthCallback: boolean;
	oauthErrorHandled: boolean;
	redirectInitiated: boolean;
	isLoggingOut?: boolean;
}

/**
 * Generate a cryptographically secure OAuth state parameter.
 *
 * The state parameter is used for CSRF protection in OAuth flows.
 * This generates a URL-safe random string using the Web Crypto API
 * when available, with a fallback for environments without it.
 *
 * @returns A random string suitable for use as an OAuth state parameter
 */
export function generateOAuthState(): string {
	// Check for Web Crypto API availability
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (typeof window !== 'undefined' && window.crypto != null) {
		// Use Web Crypto API for cryptographically secure random values
		const array = new Uint8Array(24);
		window.crypto.getRandomValues(array);
		// Convert to base64url (URL-safe base64)
		return btoa(String.fromCharCode(...array))
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=/g, '');
	}

	// Fallback for environments without Web Crypto API
	// This is less secure but works everywhere
	return (
		Math.random().toString(36).substring(2) +
		Math.random().toString(36).substring(2)
	);
}

/**
 * Parse OAuth callback parameters from a URL search string.
 *
 * @param search - The URL search string (e.g., window.location.search)
 * @returns Parsed OAuth callback parameters
 */
export function parseOAuthCallback(search: string): OAuthCallbackParams {
	const params = new URLSearchParams(search);

	return {
		code: params.get('code'),
		state: params.get('state'),
		error: params.get('error'),
		errorDescription: params.get('error_description'),
	};
}

/**
 * Check if the current URL has OAuth callback parameters.
 *
 * @param search - The URL search string (e.g., window.location.search)
 * @returns True if the URL contains OAuth callback parameters
 */
export function hasOAuthCallbackParams(search: string): boolean {
	const params = parseOAuthCallback(search);
	return (params.code != null && params.state != null) || params.error != null;
}

/**
 * Check if the URL has a valid OAuth code callback (code + state).
 *
 * @param search - The URL search string
 * @returns True if URL has both code and state parameters
 */
export function hasOAuthCodeCallback(search: string): boolean {
	const params = parseOAuthCallback(search);
	return params.code != null && params.state != null;
}

/**
 * Check if an OAuth error represents a user-initiated cancellation.
 *
 * User cancellations include:
 * - Clicking "Cancel" or "Deny" on the authorization page
 * - Choosing to sign in as a different user
 * - Closing the OAuth popup/window
 *
 * @param error - The OAuth error code
 * @param errorDescription - The OAuth error description
 * @returns True if this is a user cancellation
 */
export function isUserCancellation(
	error: string | null,
	errorDescription: string | null,
): boolean {
	if (error == null) {
		return false;
	}

	// Standard OAuth error codes for user denial
	if (error === 'access_denied' || error === 'user_cancelled') {
		return true;
	}

	// Check error description for cancellation indicators
	if (errorDescription != null) {
		const lowerDesc = errorDescription.toLowerCase();
		if (
			lowerDesc.includes('cancel') ||
			lowerDesc.includes('denied') ||
			(lowerDesc.includes('user') && lowerDesc.includes('reject'))
		) {
			return true;
		}
	}

	return false;
}

/**
 * Classify an OAuth error to determine how it should be handled.
 *
 * @param error - The OAuth error code
 * @param errorDescription - The OAuth error description
 * @param errorMessage - Additional error message (e.g., from catch block)
 * @returns Classification of the error with handling recommendations
 */
export function classifyOAuthError(
	error: string | null,
	errorDescription: string | null,
	errorMessage?: string,
): OAuthErrorClassification {
	// Check for user cancellation first
	if (isUserCancellation(error, errorDescription)) {
		return {
			isUserCancellation: true,
			isConfigurationError: false,
			isPKCEError: false,
			isCodeError: false,
			isRecoverable: true,
			message: 'Authentication was cancelled',
		};
	}

	// Check for configuration errors (redirect URI issues)
	const isConfigError =
		error === 'invalid_redirect_uri' ||
		errorDescription?.includes('redirect') === true;

	if (isConfigError) {
		return {
			isUserCancellation: false,
			isConfigurationError: true,
			isPKCEError: false,
			isCodeError: false,
			isRecoverable: false,
			message: 'Invalid redirect configuration. Please contact support.',
		};
	}

	// Check for PKCE errors (recoverable by restarting flow)
	const combinedMessage =
		`${errorDescription ?? ''} ${errorMessage ?? ''}`.toLowerCase();
	const isPKCE =
		combinedMessage.includes('pkce') ||
		combinedMessage.includes('code_verifier') ||
		combinedMessage.includes('code_challenge');

	if (isPKCE) {
		return {
			isUserCancellation: false,
			isConfigurationError: false,
			isPKCEError: true,
			isCodeError: false,
			isRecoverable: true,
			message: 'Authentication session expired. Please try again.',
		};
	}

	// Check for authorization code errors (recoverable by restarting flow)
	const isCodeErr =
		combinedMessage.includes('authorization code') ||
		combinedMessage.includes('invalid_grant') ||
		combinedMessage.includes('code has expired');

	if (isCodeErr) {
		return {
			isUserCancellation: false,
			isConfigurationError: false,
			isPKCEError: false,
			isCodeError: true,
			isRecoverable: true,
			message: 'Authentication session expired. Please try again.',
		};
	}

	// Default: unrecoverable error
	return {
		isUserCancellation: false,
		isConfigurationError: false,
		isPKCEError: false,
		isCodeError: false,
		isRecoverable: false,
		message: errorDescription ?? errorMessage ?? 'Authentication failed',
	};
}

/**
 * Check if an OAuth redirect should be initiated.
 *
 * This function encapsulates the complex logic for determining when
 * to redirect to the OAuth authorization server.
 *
 * @param options - Current state options
 * @returns True if redirect should be initiated
 */
export function shouldInitiateOAuthRedirect(
	options: OAuthRedirectCheckOptions,
): boolean {
	const {
		isInitializing,
		isProcessingCallback,
		isAuthenticated,
		isLoading,
		hasOAuthCallback,
		oauthErrorHandled,
		redirectInitiated,
		isLoggingOut,
	} = options;

	// Don't redirect if logout is in progress - let the page reload handle fresh OAuth flow
	if (isLoggingOut === true) {
		return false;
	}

	// Don't redirect if still initializing
	if (isInitializing) {
		return false;
	}

	// Don't redirect if already processing a callback
	if (isProcessingCallback) {
		return false;
	}

	// Don't redirect if already authenticated
	if (isAuthenticated) {
		return false;
	}

	// Don't redirect if auth is loading (e.g., token refresh in progress)
	if (isLoading) {
		return false;
	}

	// Don't redirect if we have callback params to process
	if (hasOAuthCallback) {
		return false;
	}

	// Don't redirect if we've already handled an error (prevents loops)
	if (oauthErrorHandled) {
		return false;
	}

	// Don't redirect if we've already initiated a redirect
	if (redirectInitiated) {
		return false;
	}

	return true;
}

/**
 * Clear OAuth callback parameters from the current URL.
 *
 * @param newPath - Optional new path to navigate to (defaults to '/')
 */
export function clearOAuthCallbackParams(newPath: string = '/'): void {
	if (typeof window === 'undefined') {
		return;
	}

	const newUrl = `${window.location.origin}${newPath}`;
	window.history.replaceState({}, document.title, newUrl);
}

/**
 * PD-17462 — the page a signed-out person was opening, kept across the
 * hosted sign-in round trip so they land there afterwards instead of on the
 * dashboard.
 *
 * Kept in sessionStorage under a key WITHOUT the `workd:` prefix on purpose:
 * the sign-in flow clears every `workd:` key more than once (a cancelled
 * sign-in, a PKCE retry, a forced sign-out), and the destination has to
 * survive those. sessionStorage is per tab, so a link opened in one tab
 * never redirects another.
 */
const SIGN_IN_DESTINATION_KEY = 'signin:destination';

/**
 * A destination older than this is ignored, so a sign-in someone abandoned
 * does not send a later, plain visit to the site root to that old page.
 */
export const SIGN_IN_DESTINATION_MAX_AGE_MS = 60 * 60 * 1000;

/** Query parameters that belong to the sign-in flow, never to the page. */
const SIGN_IN_QUERY_PARAMS: ReadonlyArray<string> = [
	'code',
	'state',
	'error',
	'error_description',
	'sign_in_token',
];

export interface SignInLocationModel {
	pathname: string;
	search: string;
	hash: string;
}

interface StoredSignInDestinationModel {
	path: string;
	savedAt: number;
}

/**
 * The in-app address for a location, or null when there is nothing worth
 * returning to (the site root) or the location must never be a destination.
 *
 * Only same-origin paths are accepted: a single leading "/". Browsers treat
 * "//host" and "/\host" as another site, so those would be open redirects.
 * The OAuth callback pages are refused, and the sign-in flow's own query
 * parameters are dropped so a destination never replays a callback.
 */
export function toSignInDestination(
	location: SignInLocationModel,
): string | null {
	const { pathname } = location;

	if (
		!pathname.startsWith('/') ||
		pathname.startsWith('//') ||
		pathname.startsWith('/\\') ||
		pathname === '/' ||
		pathname === '/oauth' ||
		pathname.startsWith('/oauth/')
	) {
		return null;
	}

	const params = new URLSearchParams(location.search);

	for (const name of SIGN_IN_QUERY_PARAMS) {
		params.delete(name);
	}

	const query = params.toString();

	return `${pathname}${query !== '' ? `?${query}` : ''}${location.hash}`;
}

/** sessionStorage when the platform has one and it can be used. */
function getSessionStorage(): Storage | null {
	try {
		return typeof window !== 'undefined' && window.sessionStorage != null
			? window.sessionStorage
			: null;
	} catch {
		// Access can throw where storage is blocked.
		return null;
	}
}

/**
 * Remember where to go after signing in. A location with no destination (the
 * site root) leaves an earlier one in place: a forced sign-out remembers the
 * page and then reloads the root, and the sign-in that follows must still
 * find it.
 *
 * @returns True when a destination was stored
 */
export function rememberSignInDestination(
	location: SignInLocationModel,
	now: number = Date.now(),
): boolean {
	const path = toSignInDestination(location);
	const storage = getSessionStorage();

	if (path == null || storage == null) {
		return false;
	}

	try {
		const stored: StoredSignInDestinationModel = { path, savedAt: now };
		storage.setItem(SIGN_IN_DESTINATION_KEY, JSON.stringify(stored));

		return true;
	} catch {
		// Quota or private mode: the person lands on the dashboard as before.
		return false;
	}
}

/** Drop any remembered destination (an explicit sign-out). */
export function forgetSignInDestination(): void {
	try {
		getSessionStorage()?.removeItem(SIGN_IN_DESTINATION_KEY);
	} catch {
		// Nothing to clean up when storage is unavailable.
	}
}

/**
 * The remembered destination, removed so it is used once. Null when there is
 * none, it has expired, or it no longer passes the same-origin check.
 */
export function takeSignInDestination(now: number = Date.now()): string | null {
	const storage = getSessionStorage();

	if (storage == null) {
		return null;
	}

	let raw: string | null = null;

	try {
		raw = storage.getItem(SIGN_IN_DESTINATION_KEY);
		storage.removeItem(SIGN_IN_DESTINATION_KEY);
	} catch {
		return null;
	}

	if (raw == null) {
		return null;
	}

	try {
		const stored = JSON.parse(raw) as Partial<StoredSignInDestinationModel>;

		if (
			typeof stored.path !== 'string' ||
			typeof stored.savedAt !== 'number' ||
			now - stored.savedAt > SIGN_IN_DESTINATION_MAX_AGE_MS ||
			now < stored.savedAt
		) {
			return null;
		}

		// Checked again on the way out: the stored text is only trusted as far
		// as it still reads as one of our own paths.
		const url = new URL(stored.path, 'https://workd.invalid');

		return url.origin === 'https://workd.invalid'
			? toSignInDestination({
					pathname: url.pathname,
					search: url.search,
					hash: url.hash,
				})
			: null;
	} catch {
		return null;
	}
}
